import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone as PhoneIcon, CheckCircle2, AlertTriangle, Info, X } from "lucide-react";
import AuthCard from "@/components/jea/AuthCard";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, extractApiErrorMeta, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const COOLDOWN = 30;

/** Phase 10E — Post-registration contact verification screen.
 *
 * Shows one or two 6-digit code inputs depending on what the user provided at
 * registration. Each input has an "I didn't get it" resend button with a 30s
 * cooldown. If a send previously failed (Resend quota / Twilio compliance),
 * the row shows an inline warning + lets the user defer to Settings.
 */
const CodeRow = ({ label, Icon, value, onChange, onResend, cooldown, status, destination, errorMessage }) => {
  const inputs = useRef([]);
  const digits = value.padEnd(6, " ").split("").map((c) => (c === " " ? "" : c));
  const handle = (i, raw) => {
    const d = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = d;
    onChange(next.join("").replace(/\s/g, ""));
    if (d && i < 5) inputs.current[i + 1]?.focus();
  };
  const back = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };
  return (
    <div className="space-y-2" data-testid={`verify-${label.toLowerCase()}-row`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] text-text-secondary">
          <Icon size={16} className="text-accent-pink" />
          <span>
            Code sent to <span className="text-white font-medium">{destination}</span>
          </span>
        </div>
      </div>
      {status === "degraded" || status === "send_failed" || status === "unavailable" ? (
        <div
          className={cn(
            "flex items-start gap-2 text-[13px] rounded-md px-3 py-2",
            "text-warning bg-warning/10 border border-warning/30",
          )}
          data-testid={`verify-${label.toLowerCase()}-warning`}
        >
          <AlertTriangle size={16} className="mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-warning">We couldn't send a code right now</div>
            <div
              className="text-text-secondary text-[12px] mt-0.5"
              data-testid={`verify-${label.toLowerCase()}-warning-reason`}
            >
              {errorMessage ||
                "You can finish signup and verify this method later from Settings."}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                inputMode="numeric"
                maxLength={1}
                value={digits[i]}
                onChange={(e) => handle(i, e.target.value)}
                onKeyDown={(e) => back(i, e)}
                data-testid={`verify-${label.toLowerCase()}-digit-${i}`}
                className={cn(
                  "w-11 h-14 rounded-md text-center text-xl font-bold text-white",
                  "bg-bg-tertiary border border-border-default outline-none",
                  "focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]",
                  "transition-all duration-150",
                )}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onResend}
            disabled={cooldown > 0}
            data-testid={`verify-${label.toLowerCase()}-resend`}
            className={cn(
              "text-[13px] text-text-secondary hover:text-success underline-offset-2",
              cooldown > 0 ? "opacity-50 cursor-not-allowed" : "hover:underline",
            )}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "I didn't get it — resend"}
          </button>
        </>
      )}
    </div>
  );
};

const VerifyContactPage = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { completeAuth } = useAuth();
  const { pending_token, email, phone, verification_status = {}, restored } = loc.state || {};

  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailCooldown, setEmailCooldown] = useState(COOLDOWN);
  const [phoneCooldown, setPhoneCooldown] = useState(COOLDOWN);
  const [emailStatus, setEmailStatus] = useState(verification_status.email?.status || null);
  const [phoneStatus, setPhoneStatus] = useState(verification_status.phone?.status || null);
  const [emailMessage, setEmailMessage] = useState(verification_status.email?.message || "");
  const [phoneMessage, setPhoneMessage] = useState(verification_status.phone?.message || "");
  // Phase 10F — soft-delete transparent restore banner.
  const [showRestoredBanner, setShowRestoredBanner] = useState(!!restored);

  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setInterval(() => setEmailCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [emailCooldown]);
  useEffect(() => {
    if (phoneCooldown <= 0) return;
    const t = setInterval(() => setPhoneCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [phoneCooldown]);

  if (!pending_token || (!email && !phone)) return <Navigate to="/register" replace />;

  const needsEmail = !!email && emailStatus !== "degraded" && emailStatus !== "send_failed" && emailStatus !== "unavailable";
  const needsPhone = !!phone && phoneStatus !== "degraded" && phoneStatus !== "send_failed" && phoneStatus !== "unavailable";

  const resend = async (channel) => {
    try {
      await apiClient.post("/auth/register/resend-contact-code", {
        pending_token,
        channel,
      });
      toast.success(`Code resent via ${channel}.`);
      if (channel === "email") {
        setEmailCooldown(COOLDOWN);
        setEmailStatus("sent");
      } else {
        setPhoneCooldown(COOLDOWN);
        setPhoneStatus("sent");
      }
    } catch (err) {
      const meta = extractApiErrorMeta(err);
      const msg = meta.message || formatApiError(err);
      toast.error(msg);
      if (meta.code === "email_quota_exhausted" || meta.code === "email_service_degraded") {
        setEmailStatus("degraded");
        setEmailMessage(meta.message || "");
      } else if (channel === "phone") {
        setPhoneStatus("send_failed");
        setPhoneMessage(meta.message || msg);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (needsEmail && emailCode.length !== 6) {
      setError("Enter the 6-digit code sent to your email.");
      return;
    }
    if (needsPhone && phoneCode.length !== 6) {
      setError("Enter the 6-digit code sent to your phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = { pending_token };
      if (needsEmail) payload.email_code = emailCode;
      if (needsPhone) payload.phone_code = phoneCode;
      const { data } = await apiClient.post("/auth/register/verify-contact", payload);
      completeAuth(data);
      toast.success("Contact info verified — let's set up 2FA.");
      nav("/setup-2fa", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard>
      <form onSubmit={submit} className="space-y-6" data-testid="verify-contact-form">
        {showRestoredBanner && (
          <div
            className="rounded-[12px] bg-bg-tertiary border-l-4 border-success px-4 py-3 flex items-start gap-3"
            data-testid="restored-account-banner"
            role="status"
          >
            <Info size={18} className="text-success shrink-0 mt-0.5" />
            <div className="flex-1 text-[13px] leading-relaxed">
              <div className="font-semibold text-white mb-0.5">Welcome back</div>
              <div className="text-text-secondary">
                Your account was previously deactivated and has been restored. Your old
                data is intact. If you did not request this restoration, contact{" "}
                <a href="mailto:support@judgment-ai.local" className="text-success hover:underline">
                  support@judgment-ai.local
                </a>{" "}immediately.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowRestoredBanner(false)}
              className="text-text-muted hover:text-white p-1"
              aria-label="Dismiss"
              data-testid="restored-banner-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="text-center space-y-1">
          <h1 className="text-xl font-bold text-white tracking-tight">
            Confirm your contact info
          </h1>
          <p className="text-[13px] text-text-secondary">
            {email && phone
              ? "We sent a 6-digit code to each method you provided."
              : email
              ? "We sent a 6-digit code to your email."
              : "We sent a 6-digit code to your phone via SMS."}
          </p>
        </div>

        {email && (
          <CodeRow
            label="Email"
            Icon={Mail}
            value={emailCode}
            onChange={setEmailCode}
            onResend={() => resend("email")}
            cooldown={emailCooldown}
            status={emailStatus}
            destination={email}
            errorMessage={emailMessage}
          />
        )}

        {phone && (
          <CodeRow
            label="Phone"
            Icon={PhoneIcon}
            value={phoneCode}
            onChange={setPhoneCode}
            onResend={() => resend("phone")}
            cooldown={phoneCooldown}
            status={phoneStatus}
            destination={phone}
            errorMessage={phoneMessage}
          />
        )}

        {!needsEmail && !needsPhone && (
          <div
            className="flex items-start gap-2 text-[13px] text-text-secondary bg-bg-tertiary/60 border border-border-default rounded-md px-3 py-2.5"
            data-testid="verify-defer-notice"
          >
            <CheckCircle2 size={16} className="mt-0.5 text-success" />
            <div>
              Your contact method couldn't be verified right now. You can continue
              and verify it later from Settings → Contact Methods.
            </div>
          </div>
        )}

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="verify-contact-error"
          >
            {error}
          </div>
        )}

        <JeaButton
          type="submit"
          size="lg"
          fullWidth
          loading={loading}
          data-testid="verify-contact-submit"
        >
          {!needsEmail && !needsPhone ? "Continue to 2FA setup" : "Verify and continue"}
        </JeaButton>
      </form>
    </AuthCard>
  );
};

export default VerifyContactPage;
